import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import AISummaryDialog from "@/components/app/AISummaryDialog";
import { Send, ArrowLeft, MessageSquare, Users, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";

const AVATAR_COLORS = [
  "bg-violet-500", "bg-emerald-500", "bg-amber-500",
  "bg-rose-500", "bg-sky-500", "bg-indigo-500", "bg-pink-500",
];

const colorFor = (id) => {
  if (!id) return AVATAR_COLORS[0];
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const groupByDay = (messages) => {
  const groups = [];
  let lastDay = null;
  for (const m of messages) {
    const day = new Date(m.timestamp).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    if (day !== lastDay) {
      groups.push({ day, items: [m] });
      lastDay = day;
    } else {
      groups[groups.length - 1].items.push(m);
    }
  }
  return groups;
};

const ProjectChat = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [connected, setConnected] = useState(false);
  const reconnectTimer = useRef(null);
  const reconnectDelay = useRef(1000);
  const [canWrite, setCanWrite] = useState(true);
  const [roomSize, setRoomSize] = useState(1);
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const textareaRef = useRef(null);

  const loadInitial = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/messages?limit=200`),
      ]);
      setProject(p.data);
      setMessages(m.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load chat");
    }
  }, [projectId]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  useEffect(() => {
    let destroyed = false;

    const connect = () => {
      if (destroyed) return;
      const wsBase = process.env.NODE_ENV === "development" && process.env.REACT_APP_BACKEND_URL
        ? process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, (m) => (m === "https:" ? "wss:" : "ws:"))
        : `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;
      const ws = new WebSocket(`${wsBase}/api/ws/project-chat/${projectId}`);
      wsRef.current = ws;

      ws.onopen = () => { setConnected(true); reconnectDelay.current = 1000; };
      ws.onclose = () => {
        if (destroyed) return;
        setConnected(false);
        reconnectTimer.current = setTimeout(() => {
          reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000);
          connect();
        }, reconnectDelay.current);
      };
      ws.onerror = () => setConnected(false);
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "joined") {
            setCanWrite(Boolean(payload.can_write));
            setRoomSize(payload.room_size || 1);
          } else if (payload.type === "message") {
            setMessages((prev) => [...prev, payload]);
          } else if (payload.type === "error") {
            toast.error(payload.detail || "Chat error");
          }
        } catch (e) {
          console.error("Bad WS payload", e);
        }
      };
    };

    connect();
    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer.current);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [projectId]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const send = () => {
    const content = draft.trim();
    if (!content) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error("Not connected — reconnecting…");
      return;
    }
    wsRef.current.send(JSON.stringify({ content }));
    setDraft("");
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const grouped = groupByDay(messages);

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 56px)" }}>
      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          {project && (
            <p className="text-[11px] font-semibold uppercase tracking-widest text-violet-500 mb-0.5">
              Chat · {project.name}
            </p>
          )}
          <h1 className="text-xl font-bold text-gray-900">Project Chat</h1>
          <p className="text-xs text-gray-400 mt-0.5">Real-time team channel. Only project members can see and post.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Connection status */}
          <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-testid="chat-connection-status">
            {connected
              ? <><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /><span className="text-emerald-600">Live</span></>
              : <><span className="w-2 h-2 rounded-full bg-gray-300" /><span className="text-gray-400">Connecting…</span></>
            }
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-gray-400 border border-gray-100 rounded-full px-2.5 py-1">
            <Users className="w-3.5 h-3.5" strokeWidth={1.7} />
            <span className="font-mono" data-testid="chat-room-size">{roomSize}</span>
          </span>
          <AISummaryDialog
            targetType="chat"
            projectId={projectId}
            hours={72}
            label="Catch me up"
            testId="chat-ai-summary-btn"
          />
          <Link
            to={`/workspace/${projectId}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-full text-sm font-medium text-gray-600 hover:border-violet-300 hover:text-violet-700 transition-colors"
            data-testid="back-to-project-from-chat"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Project
          </Link>
        </div>
      </div>

      {/* ── Messages ── */}
      <div
        className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-6 bg-gray-50"
        data-testid="chat-messages"
      >
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-sm">
              <div className="w-14 h-14 rounded-2xl bg-violet-100 grid place-items-center mx-auto mb-4">
                <MessageSquare className="w-7 h-7 text-violet-400" strokeWidth={1.5} />
              </div>
              <h3 className="font-semibold text-gray-700 text-base">No messages yet</h3>
              <p className="text-sm text-gray-400 mt-1">Kick off the conversation — say hello, drop a status update, or ask a question.</p>
            </div>
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.day}>
              {/* Day separator */}
              <div className="flex items-center gap-3 my-4" data-testid={`chat-day-${group.day}`}>
                <div className="h-px bg-gray-200 flex-1" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 bg-gray-50 px-2">{group.day}</span>
                <div className="h-px bg-gray-200 flex-1" />
              </div>
              <div className="space-y-4">
                {group.items.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div
                      key={m.message_id}
                      className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}
                      data-testid={`chat-msg-${m.message_id}`}
                    >
                      {/* Avatar */}
                      <div className={`w-8 h-8 rounded-xl ${colorFor(m.sender_id)} text-white text-xs grid place-items-center font-bold shrink-0 shadow-sm`}>
                        {(m.sender_name?.[0] || "?").toUpperCase()}
                      </div>
                      <div className={`max-w-[70%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                        {/* Meta */}
                        <div className={`text-[11px] text-gray-400 mb-1 flex items-center gap-2 ${mine ? "flex-row-reverse" : ""}`}>
                          <span className="font-semibold text-gray-600">{mine ? "You" : m.sender_name}</span>
                          <span>
                            {new Date(m.timestamp).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        {/* Bubble */}
                        <div className={`px-4 py-2.5 text-sm whitespace-pre-wrap break-words rounded-2xl shadow-sm ${
                          mine
                            ? "bg-violet-600 text-white rounded-tr-sm"
                            : "bg-white text-gray-800 border border-gray-100 rounded-tl-sm"
                        }`}>
                          {m.content}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      {/* ── Composer ── */}
      <div className="bg-white border-t border-gray-100 px-6 py-4 shrink-0">
        {!canWrite ? (
          <div className="text-xs text-gray-400 italic text-center py-2" data-testid="chat-readonly-notice">
            You have read-only access on this workspace. Ask an admin to upgrade your role to participate in chat.
          </div>
        ) : (
          <div className="flex gap-3 items-end">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              placeholder={connected ? "Message the team… (Enter to send, Shift+Enter for new line)" : "Reconnecting to chat…"}
              rows={2}
              className="flex-1 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-colors"
              data-testid="chat-composer-input"
            />
            <button
              onClick={send}
              disabled={!draft.trim() || !connected}
              className="h-11 w-11 rounded-full bg-violet-600 hover:bg-violet-700 text-white grid place-items-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-violet-200 shrink-0"
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
        <p className="text-[10px] text-gray-400 mt-2 text-center">
          Messages are sent to all project members · notifications delivered instantly
        </p>
      </div>
    </div>
  );
};

export default ProjectChat;
